import React from "react";
import Tabs from "@/components/controls/Tabs";
import { DreamhubGradient, Edit } from "@dreamhub/designsystem/icons";

/** Shared by the Company/Person/Lead create modals — only the tab bodies differ. */
export const DREAMER_MODE = "dreamer";
export const MANUAL_MODE = "manual";

export type DreamerMode = typeof DREAMER_MODE | typeof MANUAL_MODE;

interface Props {
  value: DreamerMode;
  onChange: (mode: DreamerMode) => void;
  labels: { withDreamer: string; manually: string };
  dataTest?: string;
}

interface ModeIconProps {
  children: React.ReactNode;
}

// Figma wants 18px but the source SVGs are 24px — fix the box, let the glyph fill it.
const ModeIcon: React.FC<ModeIconProps> = ({ children }) => (
  <span className="flex size-[18px] shrink-0 items-center justify-center [&>svg]:size-full">
    {children}
  </span>
);

// Active-bg override: design wants 12% alpha, badges-selected is 18% — see
// `segmented-switch-selected` in the design system's DESIGN.md.
/** The "With Dreamer" / "Manually" segmented switch. Flag gating is the caller's job. */
const DreamerModeTabs: React.FC<Props> = ({
  value,
  onChange,
  labels,
  dataTest
}) => {
  return (
    <Tabs
      fullWidth
      size="lg"
      value={value}
      valueSelected={(mode) => onChange(mode as DreamerMode)}
      dataTest={dataTest}
      className="data-[state=active]:bg-surface-segmented-selected data-[state=active]:border-border-badge-default"
      options={{
        [DREAMER_MODE]: (
          <span className="flex items-center justify-center gap-1">
            <ModeIcon>
              <DreamhubGradient />
            </ModeIcon>
            {labels.withDreamer}
          </span>
        ),
        [MANUAL_MODE]: (
          <span className="flex items-center justify-center gap-1">
            <ModeIcon>
              <Edit />
            </ModeIcon>
            {labels.manually}
          </span>
        )
      }}
    />
  );
};

export default DreamerModeTabs;
